import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "../../i18n/LanguageContext";
import { MaskedLine } from "../Reveal";
import { scrollToId } from "../../lib/scroll";
import SoundWave from "../SoundWave";
import { Play, Rocket } from "lucide-react";

const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="hero-vignette relative flex min-h-[100svh] items-center overflow-hidden pt-24">
      <SoundWave className="absolute inset-0 h-full w-full opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-b from-void/40 via-transparent to-void" />

      <motion.div style={{ y, opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
          className="overline-tag" data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="mt-6 font-display text-5xl font-extrabold leading-[1.05] sm:text-6xl lg:text-7xl" data-testid="hero-title">
          <MaskedLine delay={0.35}>{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.5}><span className="gold-text">{t.hero.line2}</span></MaskedLine>
          <MaskedLine delay={0.65}>{t.hero.line3}</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400 md:text-lg" data-testid="hero-sub"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button onClick={() => scrollToId("demo")} data-testid="hero-demo-cta" className="btn-gold">
            <Play size={16} /> {t.hero.ctaPrimary}
          </button>
          <button onClick={() => scrollToId("deploy")} data-testid="hero-deploy-cta" className="btn-ghost">
            <Rocket size={16} /> {t.hero.ctaSecondary}
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.4 }}
          className="mt-8 text-xs text-zinc-600" data-testid="hero-note"
        >
          {t.hero.note}
        </motion.p>
      </motion.div>
    </section>
  );
};

export default Hero;
